/**
 * A plugin's own card on a call it claims, drawn in the conversation where the
 * call was made (`docs/thin-core/liquid-page-runtime.md` §9).
 *
 * The server carries every claimed call as a `record` bubble with one read on
 * it -- the tool, its arguments, its latest answer, where it stands, and the
 * child it handed its work to, each only as far as the panel names it -- and
 * names the panel that draws it (`workbench/server/records.py`). This file is
 * the rest: the card's one line, chosen from the panel's own `states`, and the
 * panel's blocks drawn on that read. No word on a card is this shell's. The
 * line is the plugin's, the mark beside it is a tone, and a panel that says
 * nothing for a state draws no card in it.
 *
 * Two blocks read something other than the record. A `thread` block is the
 * child's own conversation, folded, read through the ordinary chat route when it
 * is opened and drawn with the conversation's own bubbles (`said`, handed in by
 * the page); a call that handed nothing to a child has no conversation to open,
 * so the block draws nothing there. A `surface` block is the plugin's own page,
 * folded the same way and started only when opened: its own pixels in the
 * sandbox, its own declared reads through the read door, and the record it sits
 * on to find its object in (`CardPage`).
 */

import { Accordion } from "../components/rumonai";
import {
  createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode,
} from "react";
import { api, type ChatMessage, type PanelEntry, type RecordPanel, type RecordRead, type SourceResult } from "../lib/api";
import { chatFeed } from "../lib/chatFeed";
import { Icon } from "../components/Icon";
import { formatClock, recordTone } from "../lib/format";
import { ErrorState, LoadingState } from "../shell/ui";
import type { ToolIndex } from "../studio/ask";
import { RecordControls } from "./RecordControls";
import { SurfaceHost } from "./SurfaceHost";
import { BlockView } from "./blocks";
import { pluck, type PanelBlock, type PanelSpec } from "./spec";
import { recordLinks } from "./recordLinks";
import { responseConversationHref } from "../shell/ownerShell";

/**
 * The record panels the shell holds now: `record_panels` on the bootstrap, then
 * on every `panels` frame. A context and not a prop because the conversation is
 * mounted in three places, and a bubble inside a card -- the child's
 * conversation -- is drawn by the same component as the page's own.
 */
export const RecordPanels = createContext<readonly RecordPanel[]>([]);

/** Adjacent ordinary calls share one compact fold; a plugin card keeps its own place. */
export function groupActivities(messages: ChatMessage[], separate?: ReadonlySet<string>): (ChatMessage | ChatMessage[])[] {
  const groups: (ChatMessage | ChatMessage[])[] = [];
  for (const message of messages) {
    const last = groups[groups.length - 1];
    if (message.kind === "record" && message.payload?.record?.panel === "" && !separate?.has(message.id)) {
      if (Array.isArray(last) && last[0].turn_id === message.turn_id) last.push(message);
      else groups.push([message]);
    } else groups.push(message);
  }
  return groups;
}

/** Exact handoff identity only. A stopped, failed or unknown call remains its own result. */
export function pendingRequestForRecord(message: ChatMessage, pending: readonly PanelEntry[]): PanelEntry | undefined {
  const state = message.kind === "record" ? message.payload?.record?.read.state : null;
  if (!message.call_id || (state !== "running" && state !== "working")) return undefined;
  return pending.find(panel => panel.interaction?.source_call_id === message.call_id
    && panel.interaction.session_id === message.session_id);
}

/** The original call stays in the timeline; its single answer surface lives with pending responses. */
export function RecordRequestTrace({ message, panel }: { message: ChatMessage; panel: PanelEntry }) {
  const interaction = panel.interaction!;
  return <article className="bubble bubble-record chat-question-trace" data-role="record" data-record-id={message.id}
    data-state={message.payload?.record?.read.state} tabIndex={-1}>
    <Icon name="question" size={16} /><span>已提出问题</span>
    <a href={responseConversationHref(interaction, { request: interaction.request_id })}>查看待答问题</a>
  </article>;
}

/**
 * The plugin cards of a turn, as the owner reads them: one notice for one thing.
 *
 * A card whose words and declared destinations match one already drawn in the same turn by
 * the same panel is the same notice again, and is said once. A failure is always drawn:
 * the shell cannot tell from a tool's name that a later call did the same thing, and a
 * failure hidden because a different write went through would say nothing failed.
 *
 * Decided from what each card says and how it stands, never from whose card it is:
 * the shell names no plugin. A call still going, and the work a child was handed,
 * is always drawn.
 */
export function settledNotices(messages: ChatMessage[], panels: readonly RecordPanel[]): ChatMessage[] {
  const cards = messages.map(message => drawnRecord(message, panels));
  const said = new Map<string, Set<string>>();
  return messages.filter((message, at) => {
    const card = cards[at];
    if (!card || !message.turn_id || isUnderway(card.read.state) || card.read.child || card.read.settlement || card.read.state === "failed") return true;
    const kind = JSON.stringify([message.turn_id, card.panel.key]);
    const words = said.get(kind) ?? new Set<string>();
    const notice = JSON.stringify([card.line, recordLinks(card.panel.spec.metadata, card.read,
      { session_id: message.session_id, call_id: message.call_id ?? "" }).map(link => link.href)]);
    if (words.has(notice)) return false;
    said.set(kind, words.add(notice));
    return true;
  });
}

const activityStates = { running: "处理中", working: "处理中", stopping: "正在停止", done: "已完成", failed: "失败", stopped: "已停止", unknown: "结果待核对" };

/**
 * A call is named by the sentence its tool declared for the Owner, quoted, or
 * else by a plain word of ours. Never by the tool's key: that is a name for
 * code, and a tool that declared nothing -- or an index that could not be read
 * -- must not put it on the Owner's page.
 */
function callName(tools: ToolIndex, tool: string, plain: string) {
  const act = tools[tool]?.act;
  return act ? <span data-quoted="true">{act}</span> : <span>{plain}</span>;
}

function activityFailure(read: RecordRead): string {
  const error = read.result?.owner_error;
  if (error && typeof error === "object" && "audience" in error && error.audience === "owner") {
    const words = ["title", "detail", "hint"].map(key => (error as Record<string, unknown>)[key])
      .filter((value): value is string => typeof value === "string" && !!value.trim()).join("；");
    return words || "详细原因尚未公开。";
  }
  return "详细原因尚未公开。";
}

export function ToolActivity({ messages, tools = {}, ownerMode = false }: {
  messages: ChatMessage[]; tools?: ToolIndex; ownerMode?: boolean;
}) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);
  const activityId = useId();
  const active = [...messages].reverse().find(item => isUnderway(item.payload!.record!.read.state));
  const activeRead = active?.payload!.record!.read;
  const failed = messages.filter(item => item.payload!.record!.read.state === "failed").length;
  const unknown = messages.filter(item => item.payload!.record!.read.state === "unknown").length;
  const unnamed = messages.filter(item => !tools[item.payload!.record!.read.tool]?.act).length;
  const shown = ownerMode && !showAll
    ? messages.filter(item => tools[item.payload!.record!.read.tool]?.act).slice(-8) : messages;
  const entries = <>
    {ownerMode ? <p className="chat-tool-activity-summary">
      {messages.length} 项操作{failed ? `，${failed} 项失败` : ""}{unknown ? `，${unknown} 项结果待核对` : ""}。
      {unnamed ? `其中 ${unnamed} 项未提供可读名称。` : ""}
    </p> : null}
    {shown.length ? <ol>{shown.map(item => {
    const read = item.payload!.record!.read;
    return <li key={item.id} data-record-id={item.id} data-state={read.state}>
      <span>{callName(tools, read.tool, "未说明的步骤")}
        {read.state === "failed" ? <span className="chat-tool-activity-reason" data-quoted="true">{activityFailure(read)}</span> : null}
      </span>
      <small>{activityStates[read.state]}</small>
    </li>;
    })}</ol> : null}
    {ownerMode && messages.length > 8 ? <button type="button" className="chat-tool-activity-more"
      onClick={() => setShowAll(value => !value)}>{showAll ? "只看有说明的最近记录" : `查看全部 ${messages.length} 项原始记录`}</button> : null}
  </>;
  // Attempts are evidence about individual calls, not the outcome of the person's task.
  // The Owner reads a compact process disclosure; an unknown receipt remains explicit,
  // and opening it preserves every attempt in order, including failures. A turn that
  // could not answer, and a plugin's own blocking work card, keep their separate notices.
  if (ownerMode) {
    const open = expanded.includes("activity");
    return <div className="chat-tool-activity" data-presentation="owner" data-state={activeRead ? activeRead.state : "settled"}>
      <button type="button" className="chat-tool-activity-toggle" id={`${activityId}-toggle`}
        aria-expanded={open} aria-controls={activityId} onClick={() => setExpanded(open ? [] : ["activity"])}>
        <span className="chat-tool-activity-title">
          <Icon name="list-check" size={15} />
          <span>{activeRead ? activeRead.state === "stopping" ? activityStates.stopping
            : callName(tools, activeRead.tool, "正在处理") : <>操作过程 · <span className="chat-tool-activity-count">{messages.length} 项{failed ? ` · ${failed} 项失败` : ""}</span></>}</span>
          {unknown ? <span className="chat-tool-activity-pending"> · 结果待确认</span> : null}
        </span>
        <Icon name={open ? "up" : "down"} size={14} />
      </button>
      <div className="chat-tool-activity-details" id={activityId} role="region" aria-labelledby={`${activityId}-toggle`} hidden={!open}>
        {open ? entries : null}
      </div>
    </div>;
  }
  return <div className="chat-tool-activity" data-state={activeRead ? activeRead.state : "settled"}>
    <Accordion value={expanded} onValueChange={setExpanded} items={[{ value: "activity", title: <span className="chat-tool-activity-title">
      <Icon name="list-check" size={16} />
      <span>{activeRead ? (activeRead.state === "stopping" ? activityStates.stopping : callName(tools, activeRead.tool, "正在使用工具"))
        : "操作记录 · " + messages.length + " 项" + (failed ? " · " + failed + " 次失败" : "") + (unknown ? " · " + unknown + " 项结果待核对" : "")}</span>
    </span>, content: entries }]} />
  </div>;
}

/** A card that is drawn: whose it is, what it was handed, and its line. */
export interface DrawnRecord {
  panel: RecordPanel;
  read: RecordRead;
  line: string;
}

const PLACEHOLDER = /\{([^{}]*)\}/g;

/**
 * A value a line can say: a non-empty string, a finite number (0 is one), or a
 * list of those, joined the way a Chinese list is. Anything else -- an object,
 * a flag, nothing -- is no value, and the phrase that asked for it is passed
 * over.
 */
function spoken(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (Array.isArray(value) && value.length) {
    const items = value.map((item) => (Array.isArray(item) ? null : spoken(item)));
    return items.every((item) => item !== null) ? items.join("、") : null;
  }
  return null;
}

const HAN = /\p{Script=Han}/u;
const LATIN = /[A-Za-z0-9]/;

/**
 * A value set among the phrase's own words, with a space where Latin letters or
 * digits meet Chinese, as the product's copy is set ("IP 设置", "找到 14 条"):
 * the phrase was written without knowing which script its value would be in.
 */
function spaced(value: string, before: string | undefined, after: string | undefined): string {
  const lead = before !== undefined && HAN.test(before) && LATIN.test(value[0]) ? " " : "";
  const tail = after !== undefined && HAN.test(after) && LATIN.test(value[value.length - 1]) ? " " : "";
  return `${lead}${value}${tail}`;
}

/** One phrase with its placeholders filled from the read, or null if one has no value. */
function fill(phrase: string, read: unknown): string | null {
  const named: string[] = [];
  let whole = true;
  const line = phrase.replace(PLACEHOLDER, (placeholder: string, path: string, at: number) => {
    const found = pluck(read, path.trim());
    const value = spoken(found);
    // A name already said earlier in the line would be said twice. Counts are
    // not names: two of them may well be equal.
    if (value === null || (typeof found !== "number" && named.includes(value))) {
      whole = false;
      return "";
    }
    if (typeof found !== "number") named.push(value);
    return spaced(value, phrase[at - 1], phrase[at + placeholder.length]);
  });
  return whole ? line : null;
}

/**
 * The card's line for the state the record is in, or null for no card.
 *
 * `states[state]` is a phrase, or phrases tried in order. `{a.b}` in a phrase
 * is a dotted path into the read, and the first phrase whose every placeholder
 * has a value is the line. A name that already stands earlier in the same line
 * counts as none, so a label that repeats the name falls through to the next
 * phrase instead of being said twice; a number is always said. An empty phrase, reached that way, means
 * no card. A state the panel does not name, or one where no phrase resolves,
 * draws the panel's title.
 */
export function recordLine(spec: Pick<PanelSpec, "title" | "states">, read: RecordRead): string | null {
  const declared = spec.states?.[read.state];
  if (declared === undefined) return spec.title;
  const phrases = typeof declared === "string" ? [declared] : declared.length ? declared : [""];
  for (const phrase of phrases) {
    if (!phrase.trim()) return null;
    const line = fill(phrase, read);
    if (line !== null) return line;
  }
  return spec.title;
}

/** Still going: drawn moving, and kept beside the composer once its turn is over. */
export function isUnderway(state: unknown): boolean {
  return state === "running" || state === "working" || state === "stopping";
}

/**
 * The card this bubble is, if one is drawn.
 *
 * Null when it is not a record; when the panel the server named is not among
 * the ones held now -- withdrawn since the conversation was read, which the page
 * answers by reading it again; or when that panel says nothing in this state.
 */
export function drawnRecord(message: ChatMessage, panels: readonly RecordPanel[]): DrawnRecord | null {
  const record = message.kind === "record" ? message.payload?.record : undefined;
  if (!record) return null;
  const panel = panels.find((candidate) => candidate.key === record.panel);
  if (!panel) return null;
  const line = recordLine(panel.spec, record.read);
  return line === null ? null : { panel, read: record.read, line };
}

/** Read-only blocks draw the record; declared controls below submit actions. */
const noAction = async () => "";

export function RecordCard({
  message,
  version,
  said,
  onToast,
  openThread = false,
}: {
  message: ChatMessage;
  /** Bumped by the one stream; what re-reads an open child's conversation. */
  version: number;
  /** The conversation's own bubbles, for what the child said, under its name. */
  said: (messages: ChatMessage[], name: string) => ReactNode;
  onToast?: (text: string, tone: "good" | "critical") => void;
  openThread?: boolean;
}) {
  const card = drawnRecord(message, useContext(RecordPanels));
  // Where a page under the card is: this conversation, this call. Its drafts
  // and saved state are kept under it, so two cards are two pages.
  const where = useMemo(() => ({ session_id: message.session_id, call_id: message.call_id ?? "" }),
    [message.session_id, message.call_id]);
  return card ? <Card id={message.id} card={card} version={version} said={said} where={where} onToast={onToast} openThread={openThread}
    controls={onToast ? <RecordControls message={message} card={card} onToast={onToast} /> : null} /> : null;
}

function Card({
  id,
  card,
  version,
  said,
  where,
  onToast,
  controls,
  openThread,
}: {
  id: string;
  card: DrawnRecord;
  version: number;
  said: (messages: ChatMessage[], name: string) => ReactNode;
  where: Record<string, string>;
  onToast?: (text: string, tone: "good" | "critical") => void;
  controls: ReactNode;
  openThread: boolean;
}) {
  const { panel, read, line } = card;
  const tone = recordTone(read.state);
  const reads = useMemo<SourceResult[]>(() => [{ type: "tail", state: "ready", value: read }], [read]);
  const links = recordLinks(panel.spec.metadata, read, where);
  const threadIndex = read.child?.session_id
    ? (panel.spec.blocks as PanelBlock[]).findIndex(block => block.kind === "thread") : -1;
  return (
    <article
      className="bubble bubble-record"
      data-role="record"
      data-testid="bubble-record"
      data-record-id={id}
      data-state={read.state}
      tabIndex={-1}
    >
      <section className="chat-record pa-open-group" data-tone={tone}>
        {threadIndex < 0 ? <p className="chat-record-line">
          {isUnderway(read.state) ? (
            <Icon className="chat-record-glyph" name={read.child ? "agent" : "list-check"} size={20} />
          ) : (
            <Icon className="chat-record-glyph" name={tone === "good" ? "done" : tone === "critical" ? "alert" : tone === "warning" ? "waiting" : tone === "accent" ? "agent" : "question"} size={20} />
          )}
          <CardLine text={line} />
        </p> : null}
        {links.map(link => <a className="btn chat-record-entry" key={`${link.label}:${link.href}`} href={link.href}>{link.label}</a>)}
        {(panel.spec.blocks as PanelBlock[]).map((block, index) =>
          block.kind === "thread" ? (
            <ChildConversation
              key={index}
              block={block}
              read={read}
              name={panel.spec.title}
              version={version}
              said={said}
              initiallyOpen={openThread}
              line={index === threadIndex ? line : undefined}
            />
          ) : block.kind === "surface" ? (
            // A page acts as well as reads, so it is offered where the card's
            // own controls are: the main conversation, connected. A child's
            // conversation is read-only (`liquid-page-runtime.md` §9).
            onToast ? <CardPage key={index} panelKey={panel.key} block={block} index={index}
              record={read} where={where} name={panel.spec.title} onToast={onToast} /> : null
          ) : block.kind === "form" ? null : (
            <BlockView
              key={index}
              block={{ ...block, actions: [] }}
              index={index}
              reads={reads}
              busy={null}
              runAction={noAction}
            />
          ),
        )}
        {controls}
      </section>
    </article>
  );
}

/**
 * The card's line, two rows at most until it is opened.
 *
 * A plugin's phrase is short, but the values set in it are whatever the call
 * carried: a memory the Agent wrote runs to hundreds of characters, and a card
 * that grew with it filled a phone's screen. A line the two rows cut off opens
 * where it stands when tapped, and closes the same way; one that fits is text.
 */
function CardLine({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);

  useLayoutEffect(() => {
    const line = ref.current;
    // Open, nothing is cut; what was is remembered, so it can close again.
    if (!line || open) return;
    const measure = () => setCut(line.scrollHeight > line.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const watcher = new ResizeObserver(measure);
    watcher.observe(line);
    return () => watcher.disconnect();
  }, [text, open]);

  const toggle = () => setOpen((was) => !was);
  const opens = cut || open ? {
    role: "button",
    tabIndex: 0,
    "aria-expanded": open,
    onClick: toggle,
    onKeyDown: (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggle();
    },
  } : {};
  return <span ref={ref} data-quoted="true" data-open={open ? "true" : undefined} {...opens}>{text}</span>;
}

/**
 * The child's own conversation, folded under the card and read only when it is
 * opened. `title` is the fold's name and `empty_text` what it says before the
 * child has said anything; `body`, when given, is a path into the read for the
 * child's name on its bubbles, and the panel's title stands in for it.
 *
 * Open, it reads again when the stream names the child's conversation: what the
 * child says and does is told under its own name, and the conversation that
 * drew the card is not read again for it (`WorkbenchService._conversations_moved`).
 */
function ChildConversation({
  block,
  read,
  name,
  version,
  said,
  initiallyOpen = false,
  line,
}: {
  block: PanelBlock;
  read: RecordRead;
  name: string;
  version: number;
  said: (messages: ChatMessage[], name: string) => ReactNode;
  initiallyOpen?: boolean;
  line?: string;
}) {
  const panels = useContext(RecordPanels);
  const session = read.child?.session_id ?? "";
  const [open, setOpen] = useState(initiallyOpen);
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [told, setTold] = useState(0);
  // What the child said is read from its start and stops at its newest line, until the reader scrolls up from it.
  const threadRef = useRef<HTMLDivElement>(null);
  const atNewest = useRef(true);
  useLayoutEffect(() => {
    const box = threadRef.current;
    if (box && atNewest.current) box.scrollTop = box.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open || !session) return;
    return chatFeed.subscribe((frame) => {
      if (frame.session_id === session) setTold((n) => n + 1);
    });
  }, [open, session]);

  useEffect(() => {
    if (!open || !session) return;
    let live = true;
    api
      .chat(session)
      .then((body) => {
        if (!live) return;
        setMessages(body.messages);
        setError(null);
      })
      .catch((err) => live && setError(err));
    return () => {
      live = false;
    };
  }, [open, session, version, told]);

  if (!session) return null;
  const speaker = (block.body ? spoken(pluck(read, block.body)) : null) ?? name;
  const tone = recordTone(read.state);
  return (
    <div className="chat-record-fold chat-record-thread" data-open={open || undefined}>
      <button type="button" className="chat-record-thread-toggle" aria-expanded={open}
        onClick={() => setOpen(value => !value)}>
        <Icon className="chat-record-glyph" name={isUnderway(read.state) ? "agent"
          : tone === "good" ? "done" : tone === "critical" ? "alert" : "waiting"} size={20} />
        <span data-quoted="true">{line || block.title || name}</span>
        <Icon name={open ? "up" : "down"} size={15} />
      </button>
      {open ? <div className="chat-record-thread-body">{error ? (
        <ErrorState error={error} />
      ) : messages === null ? (
        <LoadingState />
      ) : messages.length ? (
        <><p className="chat-record-updated">最近记录 <time>{formatClock(messages.at(-1)!.ts)}</time></p>
        <div className="chat-record-said" ref={threadRef}
          onScroll={event => {
            const box = event.currentTarget;
            atNewest.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
          }}>{said(settledNotices(messages, panels), speaker)}</div></>
      ) : block.empty_text ? (
        <p className="chat-record-quiet">{block.empty_text}</p>
      ) : null}
      </div> : null}
    </div>
  );
}

/**
 * The plugin's own page under its card, folded, and started only when opened.
 *
 * A card's line and its declarative blocks are drawn on the record alone; a
 * page is how a card shows what the record does not hold -- a research's whole
 * graph rather than its count of pages. It is an ordinary `surface`: the
 * sandbox, the bridge, and the reads its block declared, each one through the
 * read door (§7.130, §7.182). What it is told about the card is the record it
 * sits on, kept current in its snapshot (`pa.ready().record`, `record` in
 * `pa.subscribe`), so it can find its own object there the way the card's line
 * does.
 *
 * Mounted only while open: a conversation with twenty cards does not run twenty
 * pages, and opening it again is a fresh read. `title` is the fold's name.
 */
function CardPage({
  panelKey,
  block,
  index,
  record,
  where,
  name,
  onToast,
}: {
  panelKey: string;
  block: PanelBlock;
  index: number;
  record: RecordRead;
  where: Record<string, string>;
  name: string;
  onToast: (text: string, tone: "good" | "critical") => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="chat-record-fold chat-record-page" data-open={open || undefined}>
      <Accordion value={open ? ["page"] : []} onValueChange={value => setOpen(value.includes("page"))}
        items={[{ value: "page", title: block.title || name, content: open ? <SurfaceHost panelKey={panelKey} block={block} index={index} onToast={onToast}
          context={where} record={record} presentation="embedded" /> : null }]} />
    </div>
  );
}
